import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { fundingContextQuery } from '../discovery/context-query.js';
import { createOnrampRealtime } from '../realtime.js';
/** Host identity/configuration owns the connection; provider environment comes from the API. */
export function useOnrampRealtime(client, options) {
  const {
    server,
    subject,
    network,
    active = true
  } = options;
  const onEvent = useRef(options.onEvent);
  onEvent.current = options.onEvent;
  const onError = useRef(options.onError);
  onError.current = options.onError;
  const enabled = Boolean(client && server && subject && active);
  const context = useQuery({
    ...(client ? fundingContextQuery(client) : {}),
    queryKey: ['onramp', client?.cacheKey, 'context'],
    queryFn: ({
      signal
    }) => {
      if (!client) throw new Error('onramp_client_disposed');
      return client.getContext(signal);
    },
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    gcTime: 60_000,
    meta: {
      persist: false
    }
  });
  const environment = context.data?.environment;
  const key = enabled ? `${client?.cacheKey}:${server}:${subject}:${network}:${environment}` : '';
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    if (enabled && context.isError) onError.current('onramp_realtime_unavailable');
  }, [enabled, context.isError]);
  useEffect(() => {
    if (!client || !server || !subject || !active || !environment) return;
    try {
      const value = createOnrampRealtime({
        client,
        server,
        subject,
        network,
        environment,
        onEvent: event => onEvent.current?.(event),
        onError: code => onError.current(code)
      });
      setCurrent({
        key,
        value
      });
      return () => value.dispose();
    } catch {
      onError.current('onramp_realtime_unavailable');
      return;
    }
  }, [client, server, subject, network, active, environment, key]);
  return current?.key === key ? current.value : undefined;
}